import { useMemo } from "react";
import type { Message } from "../../../adapters/cla/state/ClaState.ts";
import {
  WidgetContext,
  WidgetType,
} from "../../../feature-components/WidgetLoader/WidgetContext";
import { widgetRehypePlugin } from "../../../feature-components/WidgetLoader/widgetRehypePlugin";
import {
  widgetRemarkPlugin,
  remarkRehypeOptions,
} from "../../../feature-components/WidgetLoader/widgetRemarkPlugin";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CitationWidget } from "./widgets/CitationWidget";
import { ImageWidget } from "./widgets/ImageWidget";
import { groupCitationsByDocument } from "./groupCitationsByDocument";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks";
import { FloatingCitationWidget } from "../../../feature-components/FloatingCitation/FloatingCitationWidget.tsx";

export function MessageRenderer(
  props: Readonly<{ message: Message; chatId: string }>,
) {
  const enableFloatingCitationWidget = useFeatureFlagValue(
    "enableFloatingCitationWidget",
  );
  const value = useMemo(
    () => ({
      widgets: {
        [WidgetType.Image]: ImageWidget,
        [WidgetType.Citation]: enableFloatingCitationWidget
          ? FloatingCitationWidget
          : CitationWidget,
      },
      widgetState: props.message.state?.widgets,
      messageId: props.message.id,
      chatId: props.chatId,
    }),
    [
      enableFloatingCitationWidget,
      props.message.state?.widgets,
      props.message.id,
      props.chatId,
    ],
  );

  // Group citations by document ONLY after streaming completes
  const processedContent = useMemo(() => {
    // During streaming, show individual citations
    if (props.message.status !== "completed") {
      return props.message.content;
    }

    // After streaming completes, group consecutive citations with same src
    return groupCitationsByDocument(
      props.message.content,
      props.message.state?.widgets?.citation,
    );
  }, [
    props.message.content,
    props.message.status,
    props.message.state?.widgets?.citation,
  ]);

  return (
    <WidgetContext.Provider value={value}>
      <Markdown
        remarkPlugins={[remarkGfm, widgetRemarkPlugin]}
        rehypePlugins={[widgetRehypePlugin]}
        remarkRehypeOptions={remarkRehypeOptions}
      >
        {processedContent}
      </Markdown>
    </WidgetContext.Provider>
  );
}
